import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { ApiService } from '@core/providers/http/api.service';
import { Bolseiro } from '../models/bolseiro.model';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class BolseiroService {
  private readonly path = 'bolsa';
  public params = new HttpParams();

  constructor(private apiService: ApiService) {}

  // List all bolseiros with pagination
  list(params?: any): Observable<{ data: Bolseiro[]; totalRecords: number }> {
    return this.apiService
      .get<{ data: Bolseiro[]; totalRecords: number }>(
        `${this.path}/getAllBolseiro`,
        params
          ? new HttpParams({
              fromObject: params,
            })
          : this.params,
      )
      .pipe(
        map((response) => ({
          ...response,
          data: response.data,
          totalRecords: response.totalRecords,
        })),
      );
  }

  // List all bolseiros with pagination
  list2(params?: any): Observable<any> {
    return this.apiService
      .get<{ data: Bolseiro[]; totalRecords: number }>(
        `${this.path}/getAllBolseiro`,
        params
          ? new HttpParams({
              fromObject: params,
            })
          : this.params,
      )
      .pipe(map((response) => response));
  }

  exportGerarOficio(data: any): Observable<any> {
    return this.apiService
      .post(`${this.path}/GerarOficio`, data, { responseType: 'blob' })
      .pipe(
        map((response: any) => {
          return response;
        }),
      );
  }

  exportEnquadramentoAnoLectivo(data: any): Observable<any> {
    return this.apiService
      .post(`${this.path}/Enquadramento_Ano_Lectivo`, data, {
        responseType: 'blob',
      })
      .pipe(
        map((response: any) => {
          return response;
        }),
      );
  }

  exportMemorando(data: any): Observable<any> {
    return this.apiService
      .post(`${this.path}/Memorando_Pagamento_Subsídio`, data, {
        responseType: 'blob',
      })
      .pipe(
        map((data: any) => {
          return data;
        }),
      );
  }

  exportDeclaracao(data: any): Observable<any> {
    const endpoint =
      data?.idioma === 'pt'
        ? '/Memorando_Pagamento_Subsídio_Pt'
        : '/Memorando_Pagamento_Subsídio';
    return this.apiService
      .post(`${this.path}${endpoint}`, data, {
        responseType: 'blob',
      })
      .pipe(
        map((response: any) => {
          return response;
        }),
      );
  }

  exportCancelamentoBolsa(data: any): Observable<any> {
    return this.apiService
      .post(`${this.path}/Cancelamento_de_Bolsa`, data, {
        responseType: 'blob',
      })
      .pipe(
        map((response: any) => {
          return response;
        }),
      );
  }

  exportMemorandoApoioDefesaMonografia(data: any): Observable<any> {
    return this.apiService
      .post(`${this.path}/Memorando_Apoio_Defesa_Monografia`, data, {
        responseType: 'blob',
      })
      .pipe(
        map((response: any) => {
          return response;
        }),
      );
  }

  // Get bolseiro by ID
  public show(id: string): Observable<any> {
    return this.apiService
      .get<{ data: Bolseiro }>(`${this.path}/getBolseiroById/${id}`)
      .pipe(map((response) => response));
  }

  /*
  public store(form: Bolseiro): Observable<any> {
    const bolseiro = form;
    return this.apiService
      .post<{ object: Bolseiro }>(`${this.path}/AddBolseiro`, bolseiro)
      .pipe(map((response) => response));
  }*/

  // Create new Empresa do sector
  public store(formData: FormData): Observable<any> {
    return this.apiService.post<any>(
      `${this.path}/AddBolseiro`,
      formData,
      {}, // ou `undefined` se seu ApiService não exigir esse parâmetro
      false, // true = usa a URL secundária
    );
  }
  /*
  public storeWithLogo(formData: FormData): Observable<any> {
    return this.apiService.post<any>(
      `${this.path}/AddBolseiro`,
      formData,
      {},  // ou `undefined` se seu ApiService não exigir esse parâmetro
      true // true = usa a URL secundária
    );
  }*/

  // Create new Estudante
  public storeEstudante(form: Bolseiro): Observable<Bolseiro> {
    const bolseiro = form;
    return this.apiService
      .post<{ object: Bolseiro }>(`${this.path}/AddEstudante`, bolseiro)
      .pipe(map((response) => response));
  }

  public reactivar(id: string): Observable<boolean> {
    return this.apiService
      .post<{
        data: boolean;
      }>(`${this.path}/SincronizarBolseiroKeycloak/${id}`, {})
      .pipe(map((response) => response.data));
  }

  public toggleStatus(id: string): Observable<boolean> {
    return this.apiService
      .patch<{ data: boolean }>(`${this.path}/ToogleBolseiroStatus/${id}`, {})
      .pipe(map((response) => response.data));
  }

  // Update existing bolseiro
  public update(formData: FormData, id: string): Observable<any> {
    return this.apiService.put(`${this.path}/UpdateBolseiro/${id}`, formData);
  }

  public updateBolsaCompleta(formData: FormData, id: string): Observable<any> {
    return this.apiService.put(`${this.path}/UpdateBolsaCompleta`, formData);
  }

  // Delete existing bolseiro
  delete(id: string): Observable<any> {
    return this.apiService.delete(`${this.path}/DeleteBolseiroById/${id}`);
  }

  exportFichaCadastro(url?: string): Observable<any> {
    return this.apiService.download(`${this.path}/GerarComprovativo}`).pipe(
      map((data: any) => {
        return data;
      }),
    );
  }

  gerarFichaCadastro(body: any): Observable<any> {
    return this.apiService.post<any>(`${this.path}/FichaCadastro`, body, {
      responseType: 'blob' as 'json',
    });
  }

  exportOficio(id: number): Observable<any> {
    return this.apiService.download(`${this.path}/GerarOficio/${id}`).pipe(
      map((data: any) => {
        return data;
      }),
    );
  }

  getBolseiroFotoById(id: number): Observable<Blob> {
    return this.apiService.get(
      `${this.path}/GetBolseiroFotoById/${id}`,
      new HttpParams(),
      false,
      'blob',
    );
  }

  updateFotoBolseiro(id: number, file: File) {
    const formData = new FormData();
    formData.append('id', id.toString());
    formData.append('foto', file); // campo "foto" deve bater com o backend

    return this.apiService.put(`${this.path}/UpdateBolseiroFoto`, formData);
  }
}
